import { prop } from 'ramda'
import { BehaviorSubject, first, firstValueFrom, lastValueFrom, Observable, of, timer } from 'rxjs'
import { catchError, filter, map, retry, switchMap, take, tap } from 'rxjs/operators'
import { nl } from 'date-fns/locale'
import { format, parse } from 'date-fns'
import { TranslateService } from '@ngx-translate/core'
import { Apollo } from 'apollo-angular'
import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { TranslateRouterService } from '@endeavour/ngx-translate-router'

import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { AbstractControl, AsyncValidatorFn, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms'
import { Dialog } from '@angular/cdk/dialog'

import {
    AddToShoppingCartMutation,
    AddToShoppingCartMutationService,
    CreateDropshipmentOrderMutationService,
    CreateOrderMutationService,
    DefaultResponseFragment,
    DeleteConfiguredProductsFromShoppingCartMutationService,
    DeleteFromShoppingCartMutationService,
    IsOrderReferenceAvailableQueryService,
    OrderConfigurationInput,
    OrderLineInput,
    ResponseStatuses,
    ShoppingCartConfigurationFragment,
    ShoppingCartFragment,
    ShoppingCartLineFragment,
    ShoppingCartLineInput,
    ShoppingCartProductFragment,
    ShoppingCartQuery,
    ShoppingCartQueryService,
    ShoppingCartTotalItemsQueryService,
    StartCheckoutMutationService,
    UpdateConfiguredProductInShoppingCartMutationService,
    UpdateShoppingCartMutationService,
    UserFragment,
} from '@app-graphql/schema'
import { LocalizationService } from '@app-domains/localization/service/localization.service'
import { AddedToCartDialogComponent } from '@app-domains/checkout/components/added-to-cart-dialog/added-to-cart-dialog.component'
import { OutOfStockModalComponent } from '../../pages/shopping-cart/components/out-of-stock-modal/out-of-stock-modal.component'
import { OrderHeaderStep, ShipmentSegment } from '@app-domains/checkout/types/checkout.types'
import { AssortmentService, AuthService, DebtorService, SearchService } from '@app-services'
import { AnalyticsService } from '@app-domains/analytics/analytics.service'
import { Product } from '@app-domains/content-blocks/components/product-card/product-card.types'
import { CountriesEnum } from '@app-types/common.types'
import { Analytics } from '@app-domains/analytics/types/analytics.types'
import { cannotBeAddedToCart } from '../../helpers/cannot-be-added-to-cart/cannot-be-added-to-cart'
import { DialogComponent, DialogData } from '@app-domains/ui/components/dialog/dialog.component'
import { InputPublic } from '@app-domains/ui/components/input/input.component.types'

const DEFAULT_SHOPPING_CART = 'default'

@Injectable({ providedIn: 'root' })
export class CheckoutService {

    constructor(
        private authService: AuthService,
        private debtorService: DebtorService,
        private assortmentService: AssortmentService,
        private localization: LocalizationService,
        private analyticsService: AnalyticsService,
        private recentSearches: SearchService,
        private translate: TranslateService,
        private translateRouterService: TranslateRouterService,
        private shoppingCartQueryService: ShoppingCartQueryService,
        private shoppingCartTotalItemsQueryService: ShoppingCartTotalItemsQueryService,
        private isOrderReferenceAvailableQueryService: IsOrderReferenceAvailableQueryService,
        private deleteFromShoppingCartMutationService: DeleteFromShoppingCartMutationService,
        private updateShoppingCartMutationService: UpdateShoppingCartMutationService,
        private addToShoppingCartMutationService: AddToShoppingCartMutationService,
        private updateConfiguredProductMutationService: UpdateConfiguredProductInShoppingCartMutationService,
        private deleteConfiguredProductsMutationService: DeleteConfiguredProductsFromShoppingCartMutationService,
        private startCheckoutMutationService: StartCheckoutMutationService,
        private readonly createDropshipmentOrderMutationService: CreateDropshipmentOrderMutationService,
        private createOrderMutationService: CreateOrderMutationService,
        private router: Router,
        private dialog: Dialog,
    ) {
        this.referenceFormGroup.controls.reference.addAsyncValidators(
            this.referenceAvailabilityValidator(),
        )
    }

    public addedToShoppingCart$ = new BehaviorSubject<ShoppingCartLineInput[]>([])
    public outOfStockProductsPopup$ = new BehaviorSubject<Product[]>([])

    public orderStatus$ = new BehaviorSubject<ResponseStatuses | null>(null)

    public isLoading$ = new BehaviorSubject(false)
    public placingOrder: boolean = false
    public orderHeaderSteps = new BehaviorSubject<OrderHeaderStep[]>([
        {
            name: 'cart',
            isDone: true,
            isActive: false,
            link: '/',
        },
        {
            name: 'delivery',
            isDone: false,
            isActive: false,
            link: '/',
        },
        {
            name: 'review',
            isDone: false,
            isActive: false,
            link: '/',
        },
    ])

    public orderMode$ = new BehaviorSubject<'normal' | 'dropshipment'>('normal')

    public selectedItemsInShoppingCart$ = new BehaviorSubject<ShoppingCartLineFragment[]>([])

    public selectedConfigurationIdsInShoppingCart$ = new BehaviorSubject<string[]>([])

    public itemsMinimalQuantityNotReached$ = new BehaviorSubject<ShoppingCartLineFragment[]>([])

    public shoppingCart$ = new BehaviorSubject<ShoppingCartFragment>({
        cartLine: [],
        configurations: [],
        totalItems: 0,
        total: 0,
    })

    public shipments$ = new BehaviorSubject<Array<ShipmentSegment[]>>([])
    public hasOutOfStockItems = false
    public deliveryFormGroup = new FormGroup({
        name: new FormControl('', [
            Validators.required,
        ]),
        postalCode: new FormControl('', [
            Validators.required,
        ]),
        houseNumber: new FormControl('', [
            Validators.required,
        ]),
        streetName: new FormControl('', [
            Validators.required,
        ]),
        city: new FormControl('', [
            Validators.required,
        ]),
        country: new FormControl<CountriesEnum>(CountriesEnum.nl, {
            nonNullable: true,
            validators: [
                Validators.required,
            ],
        }),
        phoneNumber: new FormControl('', [
            Validators.required,
            Validators.pattern('[- +()0-9]+'),
        ]),
        email: new FormControl('', [
            Validators.required,
            Validators.email,
        ]),
    })

    public referenceFormGroup = new FormGroup({
        reference: new FormControl('', [
            Validators.required,
            Validators.maxLength(20),
        ]),
        description: new FormControl('', [
            Validators.maxLength(30),
        ]),
        addToAssortment: new FormControl(true),
    })

    public referenceErrors: InputPublic.ErrorMessageRecord = {
        referenceNotAvailable: this.translate.instant('validation.messages.reference-not-available'),
        required: this.translate.instant('validation.messages.required'),
        maxlength: this.translate.instant('validation.messages.max-length', { requiredLength: 20 }),
    }

    public descriptionErrors: InputPublic.ErrorMessageRecord = {
        maxlength: this.translate.instant('validation.messages.max-length', { requiredLength: 30 }),
    }

    public hasSelectionInShoppingCart(): boolean {
        return this.selectedItemsInShoppingCart$.getValue().length > 0
            || this.selectedConfigurationIdsInShoppingCart$.getValue().length > 0
    }

    public getSelectedConfigurations(): ShoppingCartConfigurationFragment[] {
        const selectedIds = this.selectedConfigurationIdsInShoppingCart$.getValue()

        return this.shoppingCart$.getValue().configurations
            .filter((configuration) => selectedIds.includes(configuration.id))
    }

    public getSelectedTotalPrice(): number {
        const cartLinesTotal = this.selectedItemsInShoppingCart$.getValue().reduce((acc, item) => {
            return acc + Number(item.subTotal)
        }, 0)

        const configurationsTotal = this.getSelectedConfigurations().reduce((acc, item) => {
            return acc + Number(item.total)
        }, 0)

        return cartLinesTotal + configurationsTotal
    }

    public overruleShoppingCart(): void {
        const selectedItems = this.selectedItemsInShoppingCart$.getValue()
        const selectedConfigurations = this.getSelectedConfigurations()

        this.shoppingCart$.next({
            cartLine: selectedItems,
            configurations: selectedConfigurations,
            totalItems: selectedItems.length + selectedConfigurations.length,
            total: this.getSelectedTotalPrice(),
        })
    }

    public setOrderStatus(status: ResponseStatuses): void {
        this.orderStatus$.next(status)
    }

    public deleteFromShoppingCart(
        eans: string[],
        configurationIds: string[] = [],
        cartName: string = DEFAULT_SHOPPING_CART,
    ) {
        const shoppingCart: ShoppingCartLineFragment[] = this.shoppingCart$.getValue().cartLine

        const deletedProducts: ShoppingCartLineFragment[] = shoppingCart
            .filter((scl) => eans.includes(scl.product!.ean))

        const locale = this.localization.getCurrentLocale()

        this.analyticsService.logCart(locale, deletedProducts, 'remove')

        return of(undefined).pipe(
            switchMap(() => this.deleteFromShoppingCartMutationService.mutate({
                variables: {
                    name: cartName,
                    ...(eans.length > 0 && { eans }),
                    ...(configurationIds.length > 0 && { configurationIds }),
                },
            })),
            tap((r) => {
                if (r.data?.deleteFromShoppingCart) {
                    this.shoppingCart$.next(r.data.deleteFromShoppingCart)
                }
            }),
        )
    }

    public updateConfiguredProductInShoppingCart(
        configurationId: string,
        quantity: number,
        cartName: string = DEFAULT_SHOPPING_CART,
    ) {
        return of(undefined).pipe(
            switchMap(() => this.updateConfiguredProductMutationService.mutate({
                variables: {
                    input: {
                        name: cartName,
                        configurationId,
                        quantity,
                    },
                },
            })),
            tap((r) => {
                if (r.data?.updateConfiguredProductInShoppingCart) {
                    this.shoppingCart$.next(r.data.updateConfiguredProductInShoppingCart)
                }
            }),
        )
    }

    public deleteConfiguredProductsFromShoppingCart(
        configurationIds: string[],
        cartName: string = DEFAULT_SHOPPING_CART,
    ) {
        return of(undefined).pipe(
            switchMap(() => this.deleteConfiguredProductsMutationService.mutate({
                variables: {
                    name: cartName,
                    configurationIds,
                },
            })),
            tap((r) => {
                if (r.data?.deleteConfiguredProductsFromShoppingCart) {
                    this.shoppingCart$.next(r.data.deleteConfiguredProductsFromShoppingCart)
                }
            }),
        )
    }

    public emptyShoppingCart(updateShoppingCart: boolean = false, cartName: string = DEFAULT_SHOPPING_CART) {
        const shoppingCartLines = this.shoppingCart$.getValue().cartLine

        const locale = this.localization.getCurrentLocale()

        this.analyticsService.logCart(locale, shoppingCartLines, 'remove')

        return firstValueFrom(this.shoppingCart$.pipe(
            take(1),
            map((shoppingCart) => ({
                eans: shoppingCart.cartLine.map(cartLine => cartLine.product!.ean),
                configurationIds: shoppingCart.configurations.map(configuration => configuration.id),
            })),
            switchMap(({ eans, configurationIds }) => this.deleteFromShoppingCartMutationService.mutate({
                variables: {
                    name: cartName,
                    eans: eans as string[],
                    ...(configurationIds.length > 0 && { configurationIds }),
                },
            }).pipe(
                retry(3),
            )),
            tap((response) => {
                const cart = response.data?.deleteFromShoppingCart
                if (! cart) {
                    return
                }

                // In some cases we don't want to update the local shopping-cart state as it is being used by other
                // components that still need to show it.
                // But the totalItems should always be updated for the header badge
                if (updateShoppingCart) {
                    this.shoppingCart$.next(cart)
                } else {
                    this.shoppingCart$.next({
                        ...this.shoppingCart$.getValue(),
                        totalItems: cart.totalItems,
                    })
                }
            }),
        ))
    }

    public async addToShoppingCart(
        shoppingCartLines: ShoppingCartLineInput[],
        cartName = DEFAULT_SHOPPING_CART,
    ): Promise<Apollo.MutateResult<AddToShoppingCartMutation>> {
        return lastValueFrom(of(undefined).pipe(
            tap(() => this.isLoading$.next(true)),
            switchMap(() => this.addToShoppingCartMutationService.mutate({
                variables: {
                    input: {
                        name: cartName,
                        shoppingCartLine: shoppingCartLines,
                    },
                },
            })),
            tap(() => {


            }),
            tap((r) => {
                const addedProducts = shoppingCartLines.map(prop('ean'))
                const newProducts: ShoppingCartLineFragment[] = (r.data?.addToShoppingCart?.cartLine ?? [])
                    .filter((p) => addedProducts.includes(p.product!.ean))

                const locale = this.localization.getCurrentLocale()

                this.analyticsService.logCart(locale, newProducts, 'add')
            }),
            tap((r) => {
                if (r.data?.addToShoppingCart) {
                    this.shoppingCart$.next(r.data.addToShoppingCart)
                }
            }),
            tap(() => this.isLoading$.next(false)),
            tap(() => {
                this.addedToShoppingCart$.next(shoppingCartLines)

                this.openAddedToShoppingCartSidebar()
            }),
        ))
    }

    public getShoppingCartTotalItems(cartName: string = DEFAULT_SHOPPING_CART): Promise<number> {
        return firstValueFrom(this.authService.user$.pipe(
            filter((user): user is UserFragment => !! (user && user.debtorCode)),
            switchMap(() => this.shoppingCartTotalItemsQueryService.fetch({ variables: { name: cartName } })),
            map((r) => r.data?.shoppingCart?.totalItems ?? 0),
            tap((r) => {
                let currentShoppingCart = this.shoppingCart$.getValue()

                let newShoppingCart = {
                    ...currentShoppingCart,
                    totalItems: r,
                }

                this.shoppingCart$.next(newShoppingCart)
            }),
        ))
    }

    public getShoppingCart(
        cartName: string = DEFAULT_SHOPPING_CART,
    ): Observable<Apollo.QueryResult<ShoppingCartQuery>> {
        return of(undefined).pipe(
            tap(() => this.isLoading$.next(true)),
            tap(() => this.selectedItemsInShoppingCart$.next([])),
            tap(() => this.selectedConfigurationIdsInShoppingCart$.next([])),
            switchMap(() => this.shoppingCartQueryService.fetch({
                variables: { name: cartName },
                fetchPolicy: 'no-cache',
            })),
            tap((r) => {
                if (r.data?.shoppingCart) {
                    this.shoppingCart$.next(r.data.shoppingCart)
                }
            }),
            tap(() => this.isLoading$.next(false)),
        )
    }

    public updateShoppingCart(
        shoppingCartLines: ShoppingCartLineInput[],
        cartName: string = DEFAULT_SHOPPING_CART,
    ) {
        return of(undefined).pipe(
            switchMap(() => this.updateShoppingCartMutationService.mutate({
                variables: {
                    input: {
                        name: cartName,
                        shoppingCartLine: shoppingCartLines,
                    },
                },
            })),
            tap(r => {
                if (r.data?.updateShoppingCart) {
                    this.shoppingCart$.next(r.data.updateShoppingCart)
                }
            }),
        )
    }

    public createDropshipmentOrderLines(): OrderLineInput[] {
        const shoppingCart = this.shoppingCart$.getValue()

        const orderLines: OrderLineInput[] = []

        shoppingCart.cartLine.forEach(line => {
            orderLines.push(
                {
                    ean: String(line.product?.ean),
                    quantity: Number(line.quantity),
                    delivery: String(line.product?.orderProperties?.deliveryInfo.weekYear),
                },
            )
        })

        return orderLines
    }

    /**
     * Configured products are ordered separately from the order lines, by their configuration id and with their own
     * delivery week. They are not part of the shipments, which are built from the regular cart lines only.
     */
    public createOrderConfigurations(): OrderConfigurationInput[] {
        return this.shoppingCart$.getValue().configurations.map((configuration) => ({
            configurationId: configuration.id,
            delivery: configuration.deliveryInfo.weekYear,
        }))
    }

    public createShipmentsOrderLines(): OrderLineInput[] {
        const shipments = this.shipments$.getValue()

        const orderLines: OrderLineInput[] = []

        shipments.forEach(shipment => {
            // Find the highest year in the shipment
            const highestYear = Math.max(...shipment.map(segment => segment.year))

            // Filter segments for the highest year
            const shipmentsByHighestYear = shipment.filter(line => line.year === highestYear)

            // Determine the highest week in the highest year
            const highestWeek = shipmentsByHighestYear.reduce((acc, value) => {
                return acc < value.week ? value.week : acc
            }, 0)

            shipment.forEach(segment => {
                segment.products.forEach(product => {
                    const quantity = segment.orderInfo.find(line => line.product.ean === product.ean)?.amount || 0

                    orderLines.push({
                        ean: String(product?.ean),
                        quantity: Number(quantity),
                        delivery: `${highestWeek}-${highestYear}`,
                    })
                })
            })
        })

        return orderLines
    }

    public createShipmentsFromShoppingCart(): void {
        const shoppingCart = this.shoppingCart$.getValue()

        const shipmentsNotSorted: ShipmentSegment[] = []

        // Process each line to extract shipment details
        shoppingCart.cartLine.forEach(line => {
            const deliveryInfo = line.product?.orderProperties?.deliveryInfo
            const weekYear = deliveryInfo?.weekYear || ''
            const [week, year] = weekYear.split('-').map(Number)

            if (! week && year) {
                console.error('Invalid delivery info for line', line)
                return
            }

            shipmentsNotSorted.push({
                products: [line.product as ShoppingCartProductFragment],
                amount: Number(line.quantity),
                subTotal: Number(line.subTotal),
                week,
                year,
                orderInfo: [],
            })
        })

        const shipmentsMap = new Map<string, ShipmentSegment>()

        // Merge shipments by year and week
        shipmentsNotSorted.forEach(shipment => {
            const key = `${shipment.year}-${shipment.week}`

            if (shipmentsMap.has(key)) {
                const existingShipment = shipmentsMap.get(key)!
                existingShipment.orderInfo.push({
                    product: shipment.products[0],
                    amount: shipment.amount,
                })
                existingShipment.amount += shipment.amount
                existingShipment.products.push(...shipment.products)
                existingShipment.subTotal += shipment.subTotal
            } else {
                const newShipment = { ...shipment }
                newShipment.orderInfo.push({
                    product: shipment.products[0],
                    amount: shipment.amount,
                })
                shipmentsMap.set(key, newShipment)
            }
        })

        const shipments = Array.from(shipmentsMap.values())

        shipments.sort((shipment1, shipment2) => {
            if (shipment1.year === shipment2.year) {
                return shipment1.week - shipment2.week
            }

            return shipment1.year - shipment2.year
        })

        this.shipments$.next([shipments])
    }

    public async placeDropshipmentOrder(): Promise<DefaultResponseFragment | undefined> {
        const deliveryForm = this.deliveryFormGroup.value

        const referenceForm = this.referenceFormGroup.value

        const orderLines = this.createDropshipmentOrderLines()

        const configurations = this.createOrderConfigurations()

        return await firstValueFrom(
            of(undefined).pipe(
                tap(() => this.isLoading$.next(true)),
                switchMap(() => this.createDropshipmentOrderMutationService.mutate({
                    variables: {
                        input: {
                            email: deliveryForm.email,
                            address: {
                                name: deliveryForm.name as string,
                                city: deliveryForm.city as string,
                                number: deliveryForm.houseNumber as string,
                                country: deliveryForm.country?.toUpperCase() as string,
                                phone: deliveryForm.phoneNumber as string,
                                street: deliveryForm.streetName as string,
                                zipcode: deliveryForm.postalCode as string,
                            },
                            description: referenceForm.description,
                            orderLines: orderLines,
                            ...(configurations.length > 0 && { configurations }),
                            reference: referenceForm.reference,
                            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                        },
                    },
                })),
                map((r) => r.data?.CreateDropshipmentOrder),
                tap((r) => this.setOrderStatus(r?.status || ResponseStatuses.Failed)),
                tap((r) => r?.status === 'SUCCESS' ? this.emptyShoppingCart() : ''),
                tap((r) => r?.status === 'SUCCESS' ? this.resetOrderStep() : ''),
                tap((r) => r?.status === 'SUCCESS' ? this.prepareReferenceField() : ''),
                tap(() => this.isLoading$.next(false)),
            ),
        )
    }

    public async placeNormalOrder() {
        await this.authService.refreshUser()

        const referenceForm = this.referenceFormGroup.value

        const orderLines = this.createShipmentsOrderLines()

        const configurations = this.createOrderConfigurations()

        this.isLoading$.next(true)

        const placedOrder = await firstValueFrom(
            this.authService.user$
                .pipe(
                    switchMap((user) => {
                        return this.createOrderMutationService.mutate({
                            variables: {
                                input: {
                                    email: user!.email,
                                    orderLines: orderLines,
                                    ...(configurations.length > 0 && { configurations }),
                                    reference: referenceForm.reference,
                                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                                },
                            },
                        })
                    }),
                    map((r) => {
                        if (r.error) {
                            this.setOrderStatus(ResponseStatuses.Failed)

                            const errorMessage = CombinedGraphQLErrors.is(r.error)
                                ? r.error.errors[0]?.message
                                : undefined

                            const dialogRef = this.dialog.open(DialogComponent, {
                                id: 'server-error',
                                data: {
                                    title: this.translate.instant(
                                        'order-status.sorry, something-went-wrong-with-your-order',
                                    ),
                                    message: this.translate.instant(
                                        errorMessage || 'graphql-errors.something-went-wrong-again',
                                    ),
                                } as DialogData,
                            })

                            dialogRef.closed.pipe(take(1)).subscribe(() => {
                                this.setOrderHeaderStepDoneState(['delivery'], false)

                                this.router.navigate([this.translateRouterService.translateRouteLink('/shopping-cart')])
                            })

                            return this.isLoading$.next(false)
                        }

                        return r.data?.createOrder
                    }),
                    tap((r) => this.setOrderStatus(r?.status || ResponseStatuses.Failed)),
                ),
        )

        if (referenceForm.addToAssortment) {
            const eans: string[] = orderLines.reduce((acc: string[], line) => {
                if (line.ean) {
                    acc.push(line.ean)
                }

                return acc
            }, [])

            if (eans.length > 0) {
                await firstValueFrom(
                    this.assortmentService.addToAssortment(eans as [string])
                        .pipe(retry(3)))
            }
        }

        if (placedOrder?.status === 'SUCCESS') {
            this.resetOrderStep()
            this.prepareReferenceField()
            this.isLoading$.next(false)
            return this.emptyShoppingCart()
        }

        return this.isLoading$.next(false)
    }

    public setOrderHeaderStepActiveState(stepName: 'delivery' | 'cart' | 'review', isActive: boolean): void {
        this.orderHeaderSteps.next(
            this.orderHeaderSteps.getValue().map(step => {
                step.isActive = step.name === stepName ? isActive : false
                return step
            }),
        )
    }

    public setOrderHeaderStepDoneState(stepNames: string[], isDone: boolean): void {
        const orderSteps = this.orderHeaderSteps.getValue()
        this.orderHeaderSteps.next(
            orderSteps.map(step => {
                if (stepNames.includes(step.name)) {
                    step.isDone = isDone
                }

                return step
            }),
        )
    }

    public resetOrderStep(): void {
        this.orderHeaderSteps.next([
            {
                name: 'cart',
                isDone: true,
                isActive: false,
                link: '/',
            },
            {
                name: 'delivery',
                isDone: false,
                isActive: false,
                link: '/',
            },
            {
                name: 'review',
                isDone: false,
                isActive: false,
                link: '/',
            },
        ])
    }

    public checkMinimalOrderQuantity(orderType: 'normal' | 'dropshipment' = 'normal'): void {
        const selectedItems = this.selectedItemsInShoppingCart$.getValue() as ShoppingCartLineFragment[]

        let cartLines: ShoppingCartLineFragment[] = this.shoppingCart$.getValue().cartLine as ShoppingCartLineFragment[]

        if (selectedItems.length > 0) {
            cartLines = selectedItems
        }

        const invalidQuantityOrderLines = cartLines.filter((line: ShoppingCartLineFragment) => {
            if (orderType === 'normal') {
                return (Number(line.quantity) % Number(line.product?.orderProperties?.productOrderSteps) !== 0)
            } else if (orderType === 'dropshipment') {
                return (Number(line.quantity) %
                    Number(line.product?.orderProperties?.productOrderStepDropshipment) !== 0)
            }
            return false
        })

        this.itemsMinimalQuantityNotReached$.next(invalidQuantityOrderLines)
    }

    public async startCheckout(cartName: string = DEFAULT_SHOPPING_CART): Promise<void> {
        console.log('checkoutService.startCheckout')
        const resp = await firstValueFrom(this.startCheckoutMutationService.mutate({
            variables: {
                name: cartName,
            },
        }))
        console.log('checkoutSevice.startCheckout response', resp)
    }

    public openAddedToShoppingCartSidebar(): void {
        const dialogRef = this.dialog.open(AddedToCartDialogComponent, {
            height: '400px',
            width: '600px',
            panelClass: 'add-to-shopping-cart-dialog',
        })

        dialogRef.closed.pipe(take(1)).subscribe(() => {
            this.addedToShoppingCart$.next([])
            this.outOfStockProductsPopup$.next([])
        })
    }

    public getLastDropshipmentDate(): string {
        let cartLines = this.shoppingCart$.getValue().cartLine

        if (this.selectedItemsInShoppingCart$.getValue().length > 0) {
            cartLines = this.selectedItemsInShoppingCart$.getValue()
        }

        cartLines = cartLines.sort((b, a) =>
            Number(a.product?.orderProperties?.deliveryInfo.weekNumber) -
            Number(b.product?.orderProperties?.deliveryInfo.weekNumber),
        )

        if (cartLines.length > 0) {
            const weekNumber = String(cartLines[0].product?.orderProperties?.deliveryInfo.weekNumber)

            if (! weekNumber || weekNumber === '0') {
                return ''
            }

            return format(parse(weekNumber,
                'I', new Date()), 'dd/MM/yyyy', { locale: nl })
        }

        return ''
    }

    public async checkForNotAvailableProducts(): Promise<void> {
        const cartLines = this.shoppingCart$.getValue().cartLine

        const outOfStockProducts: ShoppingCartLineFragment[] =
            cartLines.filter((x) => cannotBeAddedToCart(x.product!.price?.standardPrice!, x.product!.status))

        if (outOfStockProducts.length) {
            this.hasOutOfStockItems = true

            const dialog = this.dialog.open(OutOfStockModalComponent, { data: outOfStockProducts })

            const closedResult = await firstValueFrom(dialog.closed)

            if (closedResult === 'delete-out-of-stock') {
                const eanCodes = outOfStockProducts.map(item => item.product!.ean)

                if (eanCodes.length) {
                    await firstValueFrom(this.deleteFromShoppingCart(eanCodes as [string]))
                    this.getShoppingCart()
                    this.hasOutOfStockItems = false
                }
            }
        }
    }

    public createAnalyticsCheckoutStep(
        currentStep: number,
        event: 'begin_checkout' | 'add_shipping_info' | 'add_payment_info' | 'purchase',
        shippingTier: 'delivery' | 'dropshipment',
    ): void {
        // TODO: add item_variant

        const locale = this.localization.getCurrentLocale()

        const cartLines = this.shoppingCart$.getValue().cartLine

        const pItems: Analytics.PItem[] = cartLines.map(cartLine => {
            const item: Analytics.PItem = {
                item_id: cartLine.product!.ean,
                item_name: cartLine.product!.translations[locale].name,
                item_brand: cartLine.product!.brand?.name ?? '',
                item_category: cartLine.product?.parentCategory?.translations[locale].name,
                price: cartLine.price,
                quantity: cartLine.quantity,
                currency: 'EUR',
            }

            cartLine.product?.categories.forEach((cat, index) => {
                item[index ? 'item_category' + (index + 1) : 'item_category'] = cat.translations[locale].name
            })

            return item
        })

        const total = cartLines.reduce((acc: number, item: ShoppingCartLineFragment) => {
            return acc + Number(item!.price) * Number(item!.quantity)
        }, 0)

        this.debtorService.debtor$.pipe(first()).subscribe((debtor) => {
            if (event === 'purchase') {
                const isFreeShipping = total >= (+debtor!.paymentCondition.francoLimit * 100)
                const orderFee = 100 * Number(isFreeShipping ? 0 : debtor!.paymentCondition.orderSurcharge)
                this.analyticsService.logCheckoutStep(
                    pItems,
                    currentStep,
                    event,
                    shippingTier,
                    total + orderFee,
                    orderFee,
                )
            } else {
                this.analyticsService.logCheckoutStep(pItems, currentStep, event, shippingTier)
            }
        })
    }

    public checkReferenceForm(): void {
        Object.values(this.referenceFormGroup.controls).forEach((c) => c.markAsDirty())

        this.referenceFormGroup.markAllAsTouched()
    }

    public prepareReferenceField(): void {
        const referenceControl = this.referenceFormGroup.controls.reference

        // emitEvent: false — reset already revalidates, marks pristine/untouched, and must not
        // emit valueChanges. Subscribers mark the control as touched on valueChanges, which would
        // briefly show the "required" error after a successful order while still on the review page.
        referenceControl.reset('', { emitEvent: false })
    }

    private referenceAvailabilityValidator(): AsyncValidatorFn {
        return (control: AbstractControl): Observable<ValidationErrors | null> => {
            const value = (control.value as string)?.trim()

            if (! value) {
                return of(null)
            }

            return timer(300).pipe(
                switchMap(() => this.checkReferenceAvailability(value)),
                map((available) => available ? null : { referenceNotAvailable: true }),
                catchError(() => of(null)),
                take(1),
            )
        }
    }

    private async checkReferenceAvailability(reference: string): Promise<boolean> {
        return await firstValueFrom(this.isOrderReferenceAvailableQueryService
            .fetch({
                variables: { input: reference },
                fetchPolicy: 'network-only',
            })
            .pipe(
                map(({ data }) => !! data?.orderCheckIfReferenceIsAvailable),
            ))
    }
}
